import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Scale, Download, UserX, ClipboardList } from "lucide-react";
import { GlassCard, PillButton, SectionTitle } from "./_shared";

export default function AdminRGPD() {
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [u, a] = await Promise.all([
        api.get("/admin/users?limit=300"),
        api.get("/admin/rgpd/audit?limit=50"),
      ]);
      setUsers(u.data || []);
      setAudit(a.data || []);
    } catch {
      /* noop */
    }
  };
  useEffect(() => {
    load();
  }, []);

  const rgpdExport = async (u) => {
    setBusy("export-" + u.user_id);
    try {
      const { data } = await api.get(`/admin/rgpd/export/${u.user_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rgpd-${u.email}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Erreur export"); }
    finally { setBusy(null); }
  };

  const anonymize = async (u) => {
    if (!window.confirm(`Anonymiser ${u.email} ? Les données sont conservées mais identité scrubbed.`)) return;
    setBusy("anon-" + u.user_id);
    try {
      await api.post(`/admin/rgpd/anonymize/${u.user_id}`);
      toast.success("Utilisateur anonymisé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const filtered = q ? users.filter((u) => (u.email || "").toLowerCase().includes(q.toLowerCase())) : users;

  return (
    <div className="space-y-4">
      <SectionTitle icon={Scale}>Opérations RGPD</SectionTitle>

      <GlassCard className="p-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher utilisateur…"
          className="w-full bg-transparent text-sm text-[var(--text)] placeholder:text-zinc-400 outline-none"
          data-testid="rgpd-search"
        />
      </GlassCard>

      <GlassCard className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
        {filtered.slice(0, 50).map((u) => (
          <div key={u.user_id} className="flex items-center gap-2 p-3" data-testid={`rgpd-row-${u.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[var(--text)] truncate">{u.name || "—"}</div>
              <div className="text-[11px] text-zinc-400 truncate">{u.email}</div>
            </div>
            <PillButton onClick={() => rgpdExport(u)} disabled={busy === "export-" + u.user_id} testId={`rgpd-export-${u.user_id}`}>
              <Download className="w-3 h-3 inline mr-1" />Export
            </PillButton>
            <PillButton onClick={() => anonymize(u)} variant="danger" disabled={busy === "anon-" + u.user_id || u.email === "admin@prosperr.xyz"} testId={`rgpd-anon-${u.user_id}`}>
              <UserX className="w-3 h-3 inline mr-1" />Anon.
            </PillButton>
          </div>
        ))}
      </GlassCard>

      <div className="flex items-center gap-2 mt-6 mb-2">
        <ClipboardList className="w-4 h-4 text-[var(--text)]" />
        <div className="text-[var(--text)] font-display font-bold text-lg">Journal d'audit</div>
      </div>

      <GlassCard className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
        {audit.length === 0 ? (
          <div className="p-4 text-xs text-zinc-400 text-center">Aucun événement</div>
        ) : (
          audit.map((e, i) => (
            <div key={i} className="p-3 flex items-start gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-100">{e.event}</div>
                <div className="text-[11px] text-zinc-400 truncate">
                  {e.actor_email}{e.target_email && ` → ${e.target_email}`}
                </div>
              </div>
              <div className="text-[10px] text-zinc-400 shrink-0 tabular-nums">
                {new Date(e.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))
        )}
      </GlassCard>
    </div>
  );
}
